import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { X, XCircle, Check, Copy, Printer, Navigation, ChevronDown, Calculator } from "lucide-react";

// Custom marker icons
const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const endIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const RouteMapModal = ({ isOpen, onClose, startAddress, endAddress, startLabel, endLabel, showCosts = false }) => {
  const { t } = useTranslation();
  const [startCoords, setStartCoords] = useState(null);
  const [endCoords, setEndCoords] = useState(null);
  const [route, setRoute] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [directions, setDirections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDirections, setShowDirections] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && startAddress && endAddress) {
      setLoading(true);
      setError(null);
      setRoute(null);
      setRouteInfo(null);
      setDirections([]);
      loadRoute();
    }
  }, [isOpen, startAddress, endAddress]);

  // Icone per le manovre
  const getManeuverIcon = (type, modifier) => {
    const icons = {
      'depart': '🚗',
      'arrive': '🏁',
      'turn': modifier === 'left' ? '⬅️' : modifier === 'right' ? '➡️' : modifier === 'slight left' ? '↖️' : modifier === 'slight right' ? '↗️' : modifier === 'sharp left' ? '↩️' : modifier === 'sharp right' ? '↪️' : '➡️',
      'new name': '⬆️',
      'merge': '🔀',
      'on ramp': '🛣️',
      'off ramp': '🚦',
      'fork': modifier === 'left' ? '↙️' : '↘️',
      'end of road': modifier === 'left' ? '⬅️' : '➡️',
      'continue': '⬆️',
      'roundabout': '🔄',
      'rotary': '🔄',
      'roundabout turn': '🔄',
      'notification': 'ℹ️',
      'exit roundabout': '↗️',
      'exit rotary': '↗️'
    };
    return icons[type] || '⬆️';
  };

  // Traduci le istruzioni in italiano
  const translateInstruction = (instruction) => {
    const translations = {
      'Head': 'Parti verso',
      'Turn left': 'Svolta a sinistra',
      'Turn right': 'Svolta a destra',
      'Turn slight left': 'Svolta leggermente a sinistra',
      'Turn slight right': 'Svolta leggermente a destra',
      'Turn sharp left': 'Svolta decisa a sinistra',
      'Turn sharp right': 'Svolta decisa a destra',
      'Continue': 'Continua',
      'Keep left': 'Mantieni la sinistra',
      'Keep right': 'Mantieni la destra',
      'Merge': 'Immettiti',
      'Take the ramp': 'Prendi la rampa',
      'Enter the roundabout': 'Entra nella rotonda',
      'Exit the roundabout': 'Esci dalla rotonda',
      'At the roundabout': 'Alla rotonda',
      'take the': 'prendi la',
      'exit': 'uscita',
      'onto': 'su',
      'Arrive at': 'Arrivo a',
      'destination': 'destinazione',
      'north': 'nord',
      'south': 'sud',
      'east': 'est',
      'west': 'ovest',
      'northeast': 'nord-est',
      'northwest': 'nord-ovest',
      'southeast': 'sud-est',
      'southwest': 'sud-ovest'
    };
    
    let translated = instruction;
    Object.entries(translations).forEach(([en, it]) => {
      translated = translated.replace(new RegExp(en, 'gi'), it);
    });
    return translated;
  };

  const formatDistance = (meters) => {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(1)} km`;
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) {
      return `${Math.round(seconds)} sec`;
    }
    const mins = Math.round(seconds / 60);
    if (mins < 60) {
      return `${mins} min`;
    }
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h ${remainingMins}min`;
  };

  const geocodeAddress = async (address) => {
    // Clean and normalize the address for better geocoding results
    const cleanAddress = (addr) => {
      return addr
        .replace(/\s+/g, ' ')  // Remove extra spaces
        .replace(/,\s*,/g, ',') // Remove double commas
        .trim();
    };

    // Try multiple address formats for better results
    const tryGeocode = async (addr) => {
      try {
        // Add small delay to respect Nominatim rate limits
        await new Promise(resolve => setTimeout(resolve, 100));
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}&limit=1&countrycodes=it`,
          { headers: { 'User-Agent': 'LoJackCalendar/1.0' } }
        );
        const data = await response.json();
        if (data && data.length > 0) {
          return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        }
        return null;
      } catch (e) {
        console.error('Geocoding error:', e);
        return null;
      }
    };

    const cleaned = cleanAddress(address);
    
    // Try original address first with Italia
    let searchAddr = cleaned.toLowerCase().includes('italia') ? cleaned : cleaned + ', Italia';
    let result = await tryGeocode(searchAddr);
    if (result) return result;

    // Try removing province abbreviation (MI, RM, etc.)
    const withoutProvince = searchAddr.replace(/,\s*[A-Z]{2}\s*,/gi, ',').replace(/,\s*[A-Z]{2}\s*$/gi, '');
    if (withoutProvince !== searchAddr) {
      result = await tryGeocode(withoutProvince);
      if (result) return result;
    }

    // Try removing civic number (sometimes causes issues)
    const withoutNumber = searchAddr.replace(/,?\s*\d+\s*,/g, ',').replace(/\s+\d+\s*,/g, ',');
    if (withoutNumber !== searchAddr) {
      result = await tryGeocode(withoutNumber);
      if (result) return result;
    }

    // Try with just street name and city
    const parts = cleaned.split(',').map(p => p.trim());
    if (parts.length >= 2) {
      // Extract street (remove numbers) and city
      const street = parts[0].replace(/\d+/g, '').trim();
      const city = parts.find(p => /^[A-Za-z\s]+$/.test(p.trim()) && p.length > 2) || parts[1];
      result = await tryGeocode(`${street}, ${city}, Italia`);
      if (result) return result;
    }

    return null;
  };

  const loadRoute = async () => {
    setLoading(true);
    setError(null);

    try {
      const [start, end] = await Promise.all([
        geocodeAddress(startAddress),
        geocodeAddress(endAddress)
      ]);

      if (!start || !end) {
        setError('Impossibile trovare uno degli indirizzi');
        setLoading(false);
        return;
      }

      setStartCoords(start);
      setEndCoords(end);

      // Get route with turn-by-turn directions
      const routeResponse = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${start.lng},${start.lat};${end.lng},${end.lat}?overview=full&geometries=geojson&steps=true`
      );
      const routeData = await routeResponse.json();

      if (routeData.routes && routeData.routes.length > 0) {
        const routeCoords = routeData.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
        setRoute(routeCoords);
        
        const distanceKm = routeData.routes[0].distance / 1000;
        const durationMin = Math.round(routeData.routes[0].duration / 60);
        
        // Parse directions from steps
        const steps = routeData.routes[0].legs[0].steps;
        const parsedDirections = steps.map((step, index) => ({
          id: index,
          instruction: translateInstruction(step.maneuver.instruction || step.name || 'Continua'),
          distance: step.distance,
          duration: step.duration,
          type: step.maneuver.type,
          modifier: step.maneuver.modifier,
          name: step.name || '',
          icon: getManeuverIcon(step.maneuver.type, step.maneuver.modifier)
        }));
        setDirections(parsedDirections);
        
        // Calculate costs
        const fuelCostPerKm = 0.15;
        const tollCostPerKm = 0.08;
        const fuelCost = (distanceKm * fuelCostPerKm).toFixed(2);
        const tollCost = (distanceKm * tollCostPerKm).toFixed(2);
        const totalCost = (parseFloat(fuelCost) + parseFloat(tollCost)).toFixed(2);
        
        setRouteInfo({ 
          distance: distanceKm.toFixed(1), 
          duration: durationMin,
          fuelCost,
          tollCost,
          totalCost
        });
      }
    } catch (e) {
      console.error('Route error:', e);
      setError('Errore nel calcolo del percorso');
    } finally {
      setLoading(false);
    }
  };

  const copyDirections = () => {
    const text = `🚗 INDICAZIONI STRADALI\n` +
      `Da: ${startAddress}\n` +
      `A: ${endAddress}\n` +
      `Distanza: ${routeInfo?.distance} km | Tempo: ${formatDuration(routeInfo?.duration * 60)}\n\n` +
      directions.map((d, i) => 
        `${i + 1}. ${d.icon} ${d.instruction} (${formatDistance(d.distance)})`
      ).join('\n');
    
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const printDirections = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Indicazioni Stradali - LoJack</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #E31B23; font-size: 18px; }
            .header { margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #E31B23; }
            .info { color: #666; margin-bottom: 5px; }
            .step { display: flex; align-items: flex-start; padding: 10px 0; border-bottom: 1px solid #eee; }
            .step-num { background: #E31B23; color: white; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; margin-right: 10px; flex-shrink: 0; }
            .step-icon { font-size: 20px; margin-right: 10px; }
            .step-text { flex: 1; }
            .step-distance { color: #666; font-size: 12px; }
            .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #eee; font-size: 12px; color: #999; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🚗 Indicazioni Stradali</h1>
            <p class="info"><strong>Da:</strong> ${startAddress}</p>
            <p class="info"><strong>A:</strong> ${endAddress}</p>
            <p class="info"><strong>Distanza:</strong> ${routeInfo?.distance} km | <strong>Tempo stimato:</strong> ${routeInfo?.duration >= 60 ? Math.floor(routeInfo?.duration / 60) + 'h ' + (routeInfo?.duration % 60) + 'min' : routeInfo?.duration + ' min'}</p>
          </div>
          ${directions.map((d, i) => `
            <div class="step">
              <span class="step-num">${i + 1}</span>
              <span class="step-icon">${d.icon}</span>
              <div class="step-text">
                <div>${d.instruction}</div>
                <div class="step-distance">${formatDistance(d.distance)} • ${formatDuration(d.duration)}</div>
              </div>
            </div>
          `).join('')}
          <div class="footer">
            Generato da LoJack Italia - Powered by TelcaVoIP AI
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (!isOpen) return null;

  const center = startCoords && endCoords 
    ? [(startCoords.lat + endCoords.lat) / 2, (startCoords.lng + endCoords.lng) / 2]
    : [41.9028, 12.4964];

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gray-50 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              {showCosts ? t('appointment.calculateCosts') : t('appointment.navigate')}
            </h3>
            {routeInfo && (
              <p className="text-sm text-gray-600">
                <span className="font-medium">{routeInfo.distance} km</span> • 
                <span className="font-medium ml-1">{formatDuration(routeInfo.duration * 60)}</span>
                {directions.length > 0 && <span className="ml-2 text-gray-400">• {directions.length} passaggi</span>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {directions.length > 0 && (
              <>
                <button
                  onClick={copyDirections}
                  className="p-2 hover:bg-gray-200 rounded-lg transition flex items-center gap-1 text-sm"
                  title="Copia indicazioni"
                >
                  {copied ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
                </button>
                <button
                  onClick={printDirections}
                  className="p-2 hover:bg-gray-200 rounded-lg transition flex items-center gap-1 text-sm"
                  title="Stampa indicazioni"
                >
                  <Printer size={18} />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-lg transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* Map Container */}
          <div 
            className="relative flex-1 min-h-[300px]"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lojack-red mx-auto mb-4"></div>
                  <p className="text-gray-600">{showCosts ? 'Calcolo costi...' : 'Calcolo percorso...'}</p>
                </div>
              </div>
            ) : error ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <div className="text-center">
                  <XCircle className="text-red-500 mx-auto mb-4" size={48} />
                  <p className="text-gray-800 font-medium">{error}</p>
                  <button
                    onClick={loadRoute}
                    className="mt-4 px-4 py-2 bg-lojack-red text-white rounded-lg hover:bg-red-700 transition"
                  >
                    Riprova
                  </button>
                </div>
              </div>
            ) : (
              <MapContainer
                center={center}
                zoom={10}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {startCoords && (
                  <Marker position={[startCoords.lat, startCoords.lng]} icon={startIcon}>
                    <Popup>
                      <div className="font-medium text-green-700">📍 {startLabel || 'Partenza'}</div>
                      <div className="text-sm text-gray-600">{startAddress}</div>
                    </Popup>
                  </Marker>
                )}
                
                {endCoords && (
                  <Marker position={[endCoords.lat, endCoords.lng]} icon={endIcon}>
                    <Popup>
                      <div className="font-medium text-red-700">🏁 {endLabel || 'Arrivo'}</div>
                      <div className="text-sm text-gray-600">{endAddress}</div>
                    </Popup>
                  </Marker>
                )}
                
                {route && (
                  <Polyline
                    positions={route}
                    color="#E31B23"
                    weight={4}
                    opacity={0.8}
                  />
                )}
              </MapContainer>
            )}
          </div>

          {/* Directions Panel */}
          {directions.length > 0 && (
            <div className="w-full md:w-80 border-t md:border-t-0 md:border-l bg-white flex flex-col max-h-[300px] md:max-h-none">
              <div 
                className="p-3 bg-gray-50 border-b flex items-center justify-between cursor-pointer md:cursor-default"
                onClick={() => setShowDirections(!showDirections)}
              >
                <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Navigation size={16} className="text-lojack-red" />
                  Indicazioni Stradali
                </h4>
                <ChevronDown 
                  size={20} 
                  className={`md:hidden transition-transform ${showDirections ? 'rotate-180' : ''}`} 
                />
              </div>
              
              {showDirections && (
                <div className="flex-1 overflow-y-auto">
                  {/* Start */}
                  <div className="p-3 border-b bg-green-50">
                    <div className="flex items-start gap-3">
                      <span className="text-xl">🚗</span>
                      <div>
                        <p className="font-medium text-green-800">Parti da</p>
                        <p className="text-sm text-gray-600">{startAddress}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Steps */}
                  {directions.map((step, index) => (
                    <div 
                      key={step.id} 
                      className={`p-3 border-b hover:bg-gray-50 transition ${
                        step.type === 'arrive' ? 'bg-red-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center">
                          <span className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium">
                            {index + 1}
                          </span>
                          {index < directions.length - 1 && (
                            <div className="w-0.5 h-8 bg-gray-200 mt-1"></div>
                          )}
                        </div>
                        <span className="text-xl flex-shrink-0">{step.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800">{step.instruction}</p>
                          {step.name && step.type !== 'arrive' && (
                            <p className="text-xs text-gray-500 truncate">{step.name}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-400">{formatDistance(step.distance)}</span>
                            <span className="text-xs text-gray-300">•</span>
                            <span className="text-xs text-gray-400">{formatDuration(step.duration)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* End */}
                  <div className="p-3 bg-red-50">
                    <div className="flex items-start gap-3">
                      <span className="text-xl">🏁</span>
                      <div>
                        <p className="font-medium text-red-800">Arrivo</p>
                        <p className="text-sm text-gray-600">{endAddress}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cost Summary */}
        {showCosts && routeInfo && (
          <div className="p-4 border-t bg-green-50 flex-shrink-0">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Calculator size={18} className="text-green-600" />
              Stima Costi Viaggio
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-gray-500 uppercase">Carburante</p>
                <p className="text-xl font-bold text-gray-800">€ {routeInfo.fuelCost}</p>
                <p className="text-xs text-gray-400">~€0.15/km</p>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-gray-500 uppercase">Pedaggi (stima)</p>
                <p className="text-xl font-bold text-gray-800">€ {routeInfo.tollCost}</p>
                <p className="text-xs text-gray-400">~€0.08/km</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-green-300 bg-green-50">
                <p className="text-xs text-green-600 uppercase font-medium">Totale Stimato</p>
                <p className="text-2xl font-bold text-green-700">€ {routeInfo.totalCost}</p>
                <p className="text-xs text-gray-400">{routeInfo.distance} km</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              * I costi sono stime indicative basate su medie nazionali. I pedaggi effettivi possono variare.
            </p>
          </div>
        )}

        {/* Footer with addresses */}
        <div className="p-4 border-t bg-gray-50 flex-shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-3 h-3 bg-green-500 rounded-full mt-1.5"></div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">Partenza</p>
                <p className="text-sm text-gray-800">{startAddress}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-3 h-3 bg-red-500 rounded-full mt-1.5"></div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">Destinazione</p>
                <p className="text-sm text-gray-800">{endAddress}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteMapModal;
